import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Copy, Check, Share2 } from 'lucide-react';
import { FaWhatsapp, FaXTwitter } from 'react-icons/fa6';
import { SiGmail } from 'react-icons/si';
import './ShareMapModal.css';

const SHORT_LINK_API = 'https://f2qq5dfp52.execute-api.ap-south-1.amazonaws.com/default/create_short_link';

const ShareMapModal = ({ isOpen, onClose, routeData, searchMetadata }) => {
    const [shortLink, setShortLink] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const hasGeneratedForOpenRef = useRef(false);

    const THEME_ORANGE = '#ff5b15';

    const buildLongShareUrl = useCallback(() => {
        const baseUrl = `${window.location.origin}/shared-route`;
        const params = new URLSearchParams(window.location.search);

        if (searchMetadata?.user_src_name) {
            params.set('from', searchMetadata.user_src_name);
        }

        if (searchMetadata?.user_dest_name) {
            params.set('to', searchMetadata.user_dest_name);
        }

        if (searchMetadata?.date) {
            params.set('date', searchMetadata.date);
        }

        if (routeData) {
            try {
                const dataToEncode = {
                    ...routeData,
                    ...(searchMetadata || {})
                };

                const jsonStr = JSON.stringify(dataToEncode);
                const encodedData = btoa(unescape(encodeURIComponent(jsonStr)));
                params.set('rdata', encodedData);
            } catch (error) {
                console.warn('Route encoding failed:', error);
            }
        }

        return `${baseUrl}?${params.toString()}`;
    }, [routeData, searchMetadata]);

    const generateShortLink = useCallback(async () => {
        if (!routeData) return;

        setLoading(true);
        setCopied(false);

        const fullUrl = buildLongShareUrl();

        try {
            const response = await fetch(SHORT_LINK_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    type: 'map',
                    url: fullUrl
                })
            });

            if (!response.ok) {
                throw new Error(`Shortener API returned ${response.status}`);
            }

            const data = await response.json();
            const generatedShortUrl = data?.data?.short_url;

            if (generatedShortUrl) {
                setShortLink(generatedShortUrl);
            } else {
                setShortLink(fullUrl);
            }
        } catch (error) {
            console.error('Shortener API Error:', error);
            setShortLink(fullUrl);
        } finally {
            setLoading(false);
        }
    }, [buildLongShareUrl, routeData]);

    useEffect(() => {
        // IMPORTANT: Do not depend on routeData/searchMetadata here.
        // The parent can recreate these objects on every render, which would
        // cause this effect to fire repeatedly and spam create_short_link.
        if (!isOpen) {
            hasGeneratedForOpenRef.current = false;
            setShortLink('');
            setCopied(false);
            setLoading(false);
            return;
        }

        // Generate exactly once each time the modal is opened.
        if (!hasGeneratedForOpenRef.current && routeData) {
            hasGeneratedForOpenRef.current = true;
            generateShortLink();
        }
    }, [isOpen]);

    const handleCopy = async () => {
        if (!shortLink || loading) return;

        try {
            await navigator.clipboard.writeText(shortLink);
        } catch (error) {
            // Fallback for browsers/environments where Clipboard API is unavailable.
            const input = document.createElement('textarea');
            input.value = shortLink;
            input.style.position = 'fixed';
            input.style.left = '-9999px';
            input.style.top = '0';
            document.body.appendChild(input);
            input.focus();
            input.select();

            try {
                document.execCommand('copy');
            } catch (copyError) {
                console.error('Copy failed:', copyError);
            }

            document.body.removeChild(input);
        }

        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const shareSocial = (platform) => {
        if (!shortLink || loading) return;

        const text = 'Check out this travel route on RouteChef!';
        let url = '';

        switch (platform) {
            case 'whatsapp':
                url = `https://wa.me/?text=${encodeURIComponent(`${text} ${shortLink}`)}`;
                break;

            case 'twitter':
                url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shortLink)}`;
                break;

            case 'gmail':
                url = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(shortLink)}`;
                break;

            default:
                return;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    };

    if (!isOpen) return null;

    return (
        <div className="share-modal-overlay" onClick={onClose}>
            <div
                className={`share-modal-content ${loading ? 'modal-blur' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {loading && (
                    <div className="share-loading-overlay">
                        <div className="share-spinner"></div>
                        <p>Generating secure link...</p>
                    </div>
                )}

                <div className="share-modal-header">
                    <div className="share-header-left">
                        <Share2 size={20} className="share-icon-main" />
                        <h2>Share Route</h2>
                    </div>

                    <button
                        type="button"
                        className="share-close-btn"
                        onClick={onClose}
                        aria-label="Close share modal"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="share-body">
                    <div className="route-preview-card">
                        <div className="route-preview-info">
                            <div className="preview-locations">
                                <span className="loc-text">
                                    {searchMetadata?.user_src_name || 'Origin'}
                                </span>
                                <div className="preview-arrow">→</div>
                                <span className="loc-text">
                                    {searchMetadata?.user_dest_name || 'Destination'}
                                </span>
                            </div>

                            <p className="preview-subtext">
                                View detailed map and travel options
                            </p>
                        </div>
                    </div>

                    <div className="link-section">
                        <label htmlFor="share-route-link">Link to share</label>

                        <div className="link-input-group">
                            <input
                                id="share-route-link"
                                type="text"
                                readOnly
                                value={loading ? 'Generating link...' : shortLink}
                                className={`share-link-input ${loading ? 'input-loading' : ''}`}
                                onFocus={(e) => e.target.select()}
                            />

                            <button
                                type="button"
                                className={`copy-link-btn ${copied ? 'copied' : ''} ${loading || !shortLink ? 'btn-disabled' : ''}`}
                                onClick={handleCopy}
                                disabled={loading || !shortLink}
                            >
                                {copied ? (
                                    <>
                                        <Check size={16} />
                                        <span>COPIED</span>
                                    </>
                                ) : (
                                    <>
                                        <Copy size={16} />
                                        <span>COPY LINK</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="social-sharing">
                        <p className="social-label">Or share via</p>

                        <div className="social-buttons">
                            <button
                                type="button"
                                className={`social-btn whatsapp ${loading || !shortLink ? 'btn-disabled' : ''}`}
                                onClick={() => shareSocial('whatsapp')}
                                disabled={loading || !shortLink}
                                aria-label="Share route on WhatsApp"
                            >
                                <div className="social-icon-wrapper">
                                    <FaWhatsapp size={28} color={THEME_ORANGE} />
                                </div>
                                <span>WhatsApp</span>
                            </button>

                            <button
                                type="button"
                                className={`social-btn twitter ${loading || !shortLink ? 'btn-disabled' : ''}`}
                                onClick={() => shareSocial('twitter')}
                                disabled={loading || !shortLink}
                                aria-label="Share route on X"
                            >
                                <div className="social-icon-wrapper">
                                    <FaXTwitter size={24} color={THEME_ORANGE} />
                                </div>
                                <span>X</span>
                            </button>

                            <button
                                type="button"
                                className={`social-btn gmail ${loading || !shortLink ? 'btn-disabled' : ''}`}
                                onClick={() => shareSocial('gmail')}
                                disabled={loading || !shortLink}
                                aria-label="Share route by Gmail"
                            >
                                <div className="social-icon-wrapper">
                                    <SiGmail size={24} color={THEME_ORANGE} />
                                </div>
                                <span>Gmail</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareMapModal;
